import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Filter } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SortableHeader, TablePager } from '@/components/SortableTable';
import { Badge, Button, Card, CardContent, CardHeader } from '@/components/ui';
import { errorsApi } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { SortDirection } from '@/lib/use-table-state';
import type { ErrorType } from '@/types/api';

const TYPE_LABELS: Record<ErrorType, string> = {
  P: 'Parsing',
  T: 'RDF translation',
  H: 'Halt',
  D: 'Dedup conflict',
  V: 'Validation',
  M: 'Manual',
  N: 'Legacy NLP',
  O: 'Legacy ontology',
};

const TYPE_TONE: Record<ErrorType, string> = {
  P: 'bg-amber-100 text-amber-700',
  T: 'bg-orange-100 text-orange-700',
  H: 'bg-red-100 text-red-700',
  D: 'bg-purple-100 text-purple-700',
  V: 'bg-blue-100 text-blue-700',
  M: 'bg-slate-100 text-slate-700',
  N: 'bg-slate-100 text-slate-500',
  O: 'bg-slate-100 text-slate-500',
};

const ALL_TYPES: ErrorType[] = ['P', 'T', 'H', 'D', 'V', 'M'];

// Sort column → backend `sort` param. The API only supports a fixed
// vocabulary; columns with no backend support stay non-sortable.
const SORT_PARAM: Partial<Record<string, 'created_at' | 'error_type' | 'severity'>> = {
  errorType: 'error_type',
  severity: 'severity',
  createdAt: 'created_at',
};

export function ErrorQueue() {
  const { t } = useTranslation(['studio', 'common']);
  const [selectedTypes, setSelectedTypes] = useState<ErrorType[]>(ALL_TYPES);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortColumn, setSortColumn] = useState<keyof typeof SORT_PARAM>('createdAt');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'errors', selectedTypes, page, pageSize, sortColumn, sortDir],
    queryFn: () =>
      errorsApi.list({
        error_type: selectedTypes,
        status: ['open'],
        page,
        page_size: pageSize,
        sort: SORT_PARAM[sortColumn] ?? 'created_at',
      }),
    staleTime: 30_000,
  });

  const toggleSort = (column: keyof typeof SORT_PARAM) => {
    if (sortColumn === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortProps = (column: keyof typeof SORT_PARAM) => ({
    active: sortColumn === column,
    direction: sortDir,
    onClick: () => toggleSort(column),
  });

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // The API doesn't expose a sort-direction switch — we sort the page
  // client-side when the user picks "asc" so the UX still feels alive.
  const visibleItems = data?.items
    ? [...data.items].sort((a, b) => {
        const param = SORT_PARAM[sortColumn] ?? 'created_at';
        let av: string | number = '';
        let bv: string | number = '';
        if (param === 'created_at') {
          av = a.created_at;
          bv = b.created_at;
        } else if (param === 'error_type') {
          av = a.error_type;
          bv = b.error_type;
        } else if (param === 'severity') {
          av = a.severity ?? '';
          bv = b.severity ?? '';
        }
        const factor = sortDir === 'asc' ? 1 : -1;
        return String(av).localeCompare(String(bv), undefined, { numeric: true }) * factor;
      })
    : [];

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-display text-brand-900">{t('studio:errors.title')}</h1>
          <p className="mt-1 text-body text-slate-600">
            Resolve P/T/H/D/V/M errors before the morning commit. Decisions are
            logged to the Training Signal Store.
          </p>
        </div>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 text-h3 text-brand-900">
            <Filter className="h-4 w-4 text-slate-500" aria-hidden />
            {t('studio:errors.filters')}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {ALL_TYPES.map((type) => {
              const active = selectedTypes.includes(type);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setSelectedTypes((prev) =>
                      prev.includes(type)
                        ? prev.filter((x) => x !== type)
                        : [...prev, type],
                    );
                    setPage(1);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded border px-2 py-1 text-small transition',
                    active
                      ? 'border-brand-300 bg-brand-50 text-brand-900'
                      : 'border-slate-200 text-slate-500 hover:bg-slate-50',
                  )}
                >
                  <span className={cn('rounded px-1 font-mono', TYPE_TONE[type])}>
                    {type}
                  </span>
                  <span>{TYPE_LABELS[type]}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading && (
            <p className="px-4 py-3 text-small text-slate-500">{t('common:status.loading')}</p>
          )}
          {!isLoading && data && data.items.length === 0 && (
            <p className="px-4 py-6 text-center text-body text-slate-500">
              {t('studio:errors.noErrors')}
            </p>
          )}
          {data && data.items.length > 0 && (
            <>
              <table className="w-full text-body">
                <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
                  <tr>
                    <SortableHeader label="Type" {...sortProps('errorType')} />
                    <SortableHeader label="Subtype" active={false} />
                    <SortableHeader label="Severity" {...sortProps('severity')} />
                    <SortableHeader label="Source" active={false} />
                    <SortableHeader label="Created" {...sortProps('createdAt')} />
                    <th className="w-8 px-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleItems.map((row) => (
                    <tr key={row.error_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-small font-mono',
                            TYPE_TONE[row.error_type],
                          )}
                        >
                          {row.error_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-small text-slate-700">
                        {row.error_subtype ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-small">
                        {row.severity && (
                          <Badge
                            variant={
                              row.severity === 'BLOCKER' || row.severity === 'HIGH'
                                ? 'error'
                                : row.severity === 'MEDIUM'
                                  ? 'warning'
                                  : 'neutral'
                            }
                          >
                            {row.severity}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-small text-slate-500">
                        {row.source_id ? row.source_id.slice(0, 8) : '—'}
                      </td>
                      <td className="px-3 py-2 text-small text-slate-500">
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td className="px-2 py-2">
                        <Button asChild variant="ghost" size="icon">
                          <Link to={`/studio/errors/${row.error_id}`} aria-label="Open error">
                            <ChevronRight className="h-4 w-4" aria-hidden />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-slate-200">
                <TablePager
                  pageIndex={page - 1}
                  pageCount={pageCount}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={(p) => setPage(p + 1)}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setPage(1);
                  }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
